import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Play } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { CtaRedeApoio } from "@/components/marketing/CtaRedeApoio";
import { Separator } from "@/components/ui/separator";
import heroHome from "@/assets/images/hero-home.png";

const STEPS = [
  { n: "01", title: "Preencha seus Dados", desc: "Informe suas preferências, estilo de vida e necessidades para que o sistema entenda seu perfil." },
  { n: "02", title: "Analise sua Compatibilidade", desc: "A tecnologia cruza informações e identifica pessoas com alto nível de afinidade para dividir moradia e apoio." },
  { n: "03", title: "Validações Seguras", desc: "Documentos, histórico e buscas ajudam a garantir mais segurança e credibilidade entre os usuários.", highlight: true },
  { n: "04", title: "Receba seu Match Ideal", desc: "Você visualiza conexões adequadas ao seu perfil e escolhe com quem vai dividir a sua nova história." },
  { n: "05", title: "Contratos de Convivência", desc: "Visando mitigar as chances de desgaste na convivência." },
];

const FAQ = [
  { q: "O match é realmente seguro?", a: "Sim. Antes de apresentar qualquer possível conexão, o sistema cruza dados, realiza validações permitidas (como documentos e histórico) e aplica critérios de afinidade. Isso reduz ao máximo a chance de incompatibilidade ou experiências inseguras." },
  { q: "Tenho que aceitar todos os matchs?", a: "Não. Você tem total liberdade para escolher com quem deseja conversar e seguir adiante." },
  { q: "Quem pode usar o Mora Comigo?", a: "Qualquer pessoa em busca de moradia compartilhada segura e de uma rede de apoio confiável." },
  { q: "O que acontece depois do match?", a: "Após o match recíproco, é liberado um chat 1:1 para que vocês possam se conhecer melhor." },
];

function Stat({ n, label }) {
  return (
    <div className="text-center sm:text-left">
      <div className="text-3xl font-extrabold text-grad">{n}<span className="text-lg align-top">%</span></div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const { data: plansData } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });
  const plans = plansData?.items || [];

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mc-container pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="mc-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] text-[#1e1b2e]">
              Moradia <span className="text-grad">segura</span> e <span className="text-grad">apoio</span> com pessoas <span className="text-grad">confiáveis.</span>
            </h1>
            <p className="text-muted-foreground mt-6 max-w-md">
              Somos uma potente e inovadora ferramenta que ajuda você a se conectar com segurança e leveza à sua futura Rede de Apoio.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button className="btn-primary" data-testid="hero-signup-btn" onClick={() => navigate("/cadastro")}>Criar conta</button>
              <button className="btn-outline" data-testid="hero-how-btn" onClick={() => document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" })}>Como funciona</button>
            </div>
          </div>
          <div>
            <img src={heroHome} alt="Mora Comigo" className="w-full h-auto rounded-[28px] rounded-tr-[70px]" loading="lazy" />
          </div>
        </div>
        {/* Trust bar */}
        <div className="mc-container pb-10">
          <div className="mc-card p-6 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <Stat n="100" label="Perfis verificados" />
            <Stat n="100" label="Tecnologia confiável" />
            <Stat n="100" label="Matches seguros" />
            <div className="flex-1 md:text-right flex flex-col md:flex-row items-center justify-end gap-4">
              <span className="text-sm text-muted-foreground">Pronto para encontrar alguém compatível com você?</span>
              <button className="btn-primary" data-testid="trust-saibamais-btn" onClick={() => navigate("/planos")}>Saiba mais</button>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUÇÃO / VÍDEO */}
      <section className="mc-container py-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="relative rounded-[28px] overflow-hidden shadow-xl group">
          <img src="https://images.unsplash.com/photo-1758526213969-61068ad19a8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Solução Mora Comigo" className="w-full h-80 object-cover" loading="lazy" />
          <button className="absolute inset-0 flex items-center justify-center" data-testid="video-play-btn" aria-label="Assistir vídeo">
            <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="text-violet-600 ml-1" fill="currentColor" />
            </span>
          </button>
        </div>
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1e1b2e] leading-tight">
            Uma solução inovadora para quem busca <span className="text-grad">conexão, segurança e acolhimento.</span>
          </h2>
          <p className="text-muted-foreground mt-5">Assista ao vídeo da idealizadora e entenda como essa solução transforma vidas.</p>
        </div>
      </section>

      {/* Divisor discreto entre "solução" e "como funciona" (pedido do cliente) */}
      <div className="mc-container" data-testid="home-section-divider">
        <Separator className="mx-auto max-w-3xl bg-[#ece8f3]" />
      </div>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="mc-container py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1e1b2e]">Veja como é <span className="text-grad">fácil</span> encontrar seu <span className="text-grad">match</span></h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {STEPS.map((s) => (
            <div key={s.n} data-testid={`step-${s.n}`}
              className={`rounded-2xl p-7 border transition-transform hover:-translate-y-1 ${s.highlight ? "mc-grad-premium text-white border-transparent" : "bg-[#f8f7fc] border-[#eeebf7]"}`}>
              <div className={`text-3xl font-extrabold ${s.highlight ? "text-white" : "text-[#1e1b2e]"}`}>{s.n}</div>
              <h3 className={`text-xl font-semibold mt-4 ${s.highlight ? "text-white" : "text-[#1e1b2e]"}`}>{s.title}</h3>
              <p className={`mt-3 text-sm ${s.highlight ? "text-white/85" : "text-muted-foreground"}`}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PLANOS */}
      <section className="mc-container py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1e1b2e]">Escolha o <span className="text-grad">plano ideal</span> para você.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {plans.map((p) => <PlanCard key={p.id} plan={p} onSelect={() => navigate("/planos")} />)}
        </div>
      </section>

      {/* FAQ */}
      <section className="mc-container py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1e1b2e] mb-10">Perguntas <span className="text-grad">frequentes</span></h2>
        <div className="max-w-3xl mx-auto space-y-3">
          {FAQ.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} data-testid={`faq-${i}`}
                className={`rounded-2xl overflow-hidden border transition-colors ${open ? "mc-grad-premium border-transparent text-white" : "bg-white border-[#eeebf7]"}`}>
                <button className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left font-semibold"
                  data-testid={`faq-toggle-${i}`} onClick={() => setOpenFaq(open ? -1 : i)}>
                  <span className={open ? "text-white" : "text-[#1e1b2e]"}>{f.q}</span>
                  <ChevronDown className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} size={20} />
                </button>
                {open && <div className="px-6 pb-5 text-sm text-white/90" data-testid={`faq-answer-${i}`}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </section>

      <CtaRedeApoio />
    </div>
  );
}

export function PlanCard({ plan, onSelect }) {
  const hi = plan.highlight;
  const price = `R$${(plan.price_cents / 100).toFixed(2).replace(".", ",")}`;
  return (
    <div data-testid={`plan-card-${plan.id}`}
      className={`rounded-2xl p-7 border transition-transform hover:-translate-y-1 ${hi ? "mc-grad-premium text-white border-transparent shadow-xl" : "bg-[#f8f7fc] border-[#eeebf7]"}`}>
      <h3 className={`font-semibold ${hi ? "text-white" : "text-[#1e1b2e]"}`}>{plan.name}</h3>
      <div className="mt-4 flex items-end gap-1">
        <span className={`text-4xl font-extrabold ${hi ? "text-white" : "text-[#1e1b2e]"}`}>{price}</span>
        <span className={`text-sm mb-1 ${hi ? "text-white/80" : "text-muted-foreground"}`}>{plan.billing}</span>
      </div>
      <p className={`text-sm mt-3 ${hi ? "text-white/80" : "text-muted-foreground"}`}>{plan.tagline}</p>
      <div className={`my-5 border-t ${hi ? "border-white/25" : "border-[#e7e3f3]"}`} />
      <ul className="space-y-3">
        {plan.features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${f.enabled ? (hi ? "text-white" : "text-[#1e1b2e]") : (hi ? "text-white/40" : "text-[#c3bdd4]")}`}>
            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${f.enabled ? (hi ? "bg-white" : "bg-violet-600") : "bg-current opacity-40"}`} />
            {f.label}
          </li>
        ))}
      </ul>
      <button data-testid={`plan-select-${plan.id}`} onClick={onSelect}
        className={`w-full mt-6 ${hi ? "" : "btn-primary"}`}
        style={hi ? { background: "#fff", color: "#6d28d9", borderRadius: 9999, padding: ".7rem 1.4rem", fontWeight: 600 } : {}}>
        {plan.cta}
      </button>
    </div>
  );
}
